import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Chip,
  TablePagination,
  MenuItem,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { GeneralIds, RoutePaths } from 'types/types';
import { Patient } from 'api/models';
import { getDateString, getRoutePath } from 'utils';
import {
  Delete,
  Edit,
  AppRegistration,
  Clear,
  Visibility,
} from '@mui/icons-material';
import { getPatientName } from './utils';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useHasAccess from 'hooks/useHasAccess';
import { Permission } from 'state/permissionState';

interface PatientsTableProps {
  onFilterChange: (e: any) => void;
  filters: any;
  patients: Patient[];
  onDelete: (id: string) => void;
  totalCount: number;
  pageSize: number;
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
  loading: boolean;
}

export default function PatientsTable({
  onFilterChange,
  filters,
  patients,
  onDelete,
  totalCount,
  pageSize,
  pageNumber,
  setPageNumber,
  setPageSize,
  loading,
}: PatientsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams<GeneralIds>();
  const canViewPatient = useHasAccess([Permission.READ_PATIENT]);
  const canEditOrDeletePatient = useHasAccess([Permission.WRITE_PATIENT]);
  const canViewButCantEditPatient = canViewPatient && !canEditOrDeletePatient;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <TableContainer sx={{ mt: 3 }}>
        <Table sx={{ minHeight: '90px' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                <TextField
                  label={t('general.projectId')}
                  fullWidth
                  onChange={onFilterChange}
                  name="projectId"
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TextField
                  label={t('general.name')}
                  fullWidth
                  onChange={onFilterChange}
                  name="name"
                  size="small"
                />
              </TableCell>
              {!isMobile && (
                <TableCell sx={{ fontWeight: 'bold' }} width={'100px'}>
                  <TextField
                    label={t('general.gender')}
                    fullWidth
                    value={filters.gender ?? ''}
                    select
                    onChange={onFilterChange}
                    name="gender"
                    size="small"
                    sx={{ minWidth: '152px', p: 0 }}
                    // end adornment to clear inputs
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          size="small"
                          sx={{ display: filters.gender ? '' : 'none', mr: 2 }}
                          onClick={e =>
                            onFilterChange({
                              ...e,
                              target: {
                                value: '',
                                name: 'gender',
                              },
                            })
                          }
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  >
                    <MenuItem value="male">{t('general.male')}</MenuItem>
                    <MenuItem value="female">{t('general.female')}</MenuItem>
                    <MenuItem value="other">{t('general.other')}</MenuItem>
                  </TextField>
                </TableCell>
              )}
              {!isMobile && (
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  <TextField
                    label={t('general.birthDate')}
                    fullWidth
                    onChange={onFilterChange}
                    name="birthDate"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </TableCell>
              )}
              {!isMobile && (
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  <TextField
                    label={t('general.identifier')}
                    fullWidth
                    onChange={onFilterChange}
                    name="identifier"
                    size="small"
                  />
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                {t('general.operations')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ position: 'relative' }}>
            {loading ? (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                }}
              />
            ) : (
              patients.map(patient => (
                <TableRow
                  key={`users-table-${patient.id}-row`}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: theme => theme.palette.action.hover,
                      transition: 'background-color 0.4s',
                    },
                  }}
                >
                  <TableCell
                    sx={{ pl: 3 }}
                    component="th"
                    scope="row"
                    align="left"
                  >
                    <Link
                      to={getRoutePath(RoutePaths.patientDetails, {
                        projectId,
                        patientId: patient.id,
                      })}
                    >
                      {patient.patientProjectId}
                    </Link>
                  </TableCell>
                  <TableCell
                    sx={{ pl: 3 }}
                    component="th"
                    scope="row"
                    align="left"
                  >
                    {getPatientName(patient)}
                  </TableCell>
                  {!isMobile && (
                    <TableCell
                      sx={{ pl: 3 }}
                      component="th"
                      scope="row"
                      align="left"
                    >
                      {t(`general.${patient.gender}`)}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell
                      sx={{ pl: 3 }}
                      component="th"
                      scope="row"
                      align="left"
                    >
                      {getDateString(patient.birthDate ?? '')}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {patient.identifiers?.map(identifier => (
                        <Chip
                          key={`patient-identifier-${identifier.id}`}
                          label={`${t(`general.idTypes.${identifier.type}`)}: ${
                            identifier.id
                          }`}
                          color="primary"
                          sx={{ mr: 1, mt: 1 }}
                        />
                      ))}
                    </TableCell>
                  )}

                  <TableCell
                    align="right"
                    width={'150px'}
                    sx={{ whiteSpace: 'nowrap', pl: 3 }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigate(
                          getRoutePath(RoutePaths.patientDetails, {
                            projectId,
                            patientId: patient.id,
                          }),
                        );
                      }}
                    >
                      <Tooltip title={t('general.visits')}>
                        <AppRegistration fontSize="small" />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        canViewButCantEditPatient
                          ? navigate(
                              getRoutePath(RoutePaths.patientViewer, {
                                projectId,
                                patientId: patient.id,
                              }),
                            )
                          : navigate(
                              getRoutePath(RoutePaths.patientEditor, {
                                projectId,
                                patientId: patient.id,
                              }),
                            );
                      }}
                    >
                      {canViewButCantEditPatient ? (
                        <Tooltip title={t('general.view')}>
                          <Visibility fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('general.edit')}>
                          <Edit fontSize="small" />
                        </Tooltip>
                      )}
                    </IconButton>
                    <IconButton
                      disabled={!canEditOrDeletePatient}
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(patient.id);
                      }}
                    >
                      <Tooltip title={t('general.delete')}>
                        <Delete fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[3, 5, 10, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={pageNumber - 1}
        onPageChange={(_e, page) => setPageNumber(page + 1)}
        onRowsPerPageChange={e => {
          setPageSize(Number(e.target.value));
          setPageNumber(1);
        }}
        labelRowsPerPage={t('general.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to}, ${count} ${t('general.patient')}`
        }
      />
    </>
  );
}
